import { useState } from 'react';
import type { JSX } from 'react';
import type { Palette } from '../../../../_theme/palette.js';
import { hexWithAlpha } from '../../../../_theme/palette.js';
import type { SurveyQuestion } from '../../types.js';

interface StarRatingFieldProps {
  palette: Palette;
  question: SurveyQuestion;
  value: unknown;
  onChange: (value: number) => void;
}

// One easing curve across the whole field so the polish reads as
// intentional. Same curve used by the text input + choice rows.
const PREMIUM_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

export function StarRatingField({ palette, question, value, onChange }: StarRatingFieldProps): JSX.Element {
  const starCount = question.settings?.max ?? 5;
  const [hovered, setHovered] = useState<number | null>(null);
  const current = typeof value === 'number' ? value : 0;
  // Pre-selection state for ARIA + fill — when hovering, the row fills
  // up to (and including) the hovered star; otherwise it reflects the
  // committed value.
  const previewLevel = hovered ?? current;

  return (
    <div style={{ display: 'flex', gap: 4 }} role="radiogroup" aria-label={question.label}>
      {Array.from({ length: starCount }, (_, index) => {
        const star = index + 1;
        const filled = previewLevel >= star;
        const isCommitted = current >= star;
        return (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(null)}
            aria-label={`Rate ${star} of ${starCount}`}
            aria-checked={isCommitted}
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              // No scale on hover — it reads as jittery. The fill itself
              // is the affordance. A faint translateY lift on the
              // currently-hovered star adds physical tactility without
              // bouncing the whole row.
              transform: hovered === star ? 'translateY(-1px)' : 'translateY(0)',
              transition: `transform 220ms ${PREMIUM_EASE}`,
            }}
          >
            <svg
              viewBox="0 0 24 24"
              width="30"
              height="30"
              // Fill is the visible affordance — color transitions through
              // the muted state so unhovered stars don't snap. The transition
              // happens on color + fill so it reads as a smooth left-to-right
              // wash rather than each star independently flashing.
              style={{
                color: filled ? palette.primary : hexWithAlpha(palette.textMuted, 0.55),
                transition: `color 260ms ${PREMIUM_EASE}, fill 260ms ${PREMIUM_EASE}, stroke 260ms ${PREMIUM_EASE}`,
                fill: filled ? 'currentColor' : 'none',
                stroke: 'currentColor',
                strokeWidth: 1.5,
              }}
            >
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </button>
        );
      })}
    </div>
  );
}
